import { motion } from "framer-motion";
import { Phone, ArrowDown, Snowflake, Timer, Users, Star } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const OFFER = [
  { icon: Snowflake, text: "Monosplit posé dès 2 500 €" },
  { icon: Timer, text: "Installation sous 15 jours" },
  { icon: Users, text: "Zéro sous-traitance" },
];

const ease = [0.16, 1, 0.3, 1];

export default function ClimHero({ variant }) {
  const LINES = variant.lines;
  return (
    <section
      data-testid="clim-hero"
      data-variant={variant.key}
      className="relative overflow-hidden bg-glacier pt-28 pb-16 sm:pt-36 sm:pb-24"
    >
      <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-ice-light blur-[120px]" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-sky-200/50 blur-[120px]" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-ice/20 bg-white px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-ice sm:text-xs"
          >
            <Snowflake className="h-3.5 w-3.5" /> Climatisation réversible Daikin · Nouvelle-Aquitaine
          </motion.p>

          <h1 className="mt-7 font-display text-[clamp(2.4rem,6vw,4.6rem)] font-extrabold leading-[1.04] tracking-tight text-ink">
            {LINES.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.13, ease }}
                  className={`block ${line.accent ? "text-ice" : ""}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            {variant.sub}
          </motion.p>

          <motion.ul
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-8 flex flex-wrap gap-2.5"
            data-testid="clim-hero-offer"
          >
            {OFFER.map((o, i) => (
              <li
                key={i}
                data-testid={`clim-hero-offer-${i}`}
                className="flex items-center gap-2 rounded-full border border-glacier-border bg-white px-4 py-2 text-sm font-semibold text-ink shadow-sm"
              >
                <o.icon className="h-4 w-4 text-ice" /> {o.text}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="clim-hero-cta"
              onClick={() => scrollToId("#devis")}
              className="group flex items-center gap-2 rounded-full bg-ice px-7 py-4 text-base font-bold text-white transition-all hover:scale-[1.03] hover:bg-ice-hover hover:shadow-ice"
            >
              Obtenir mon devis gratuit
              <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </button>
            <a
              href="tel:+33535759803"
              data-testid="clim-hero-phone"
              className="flex items-center gap-2.5 rounded-full border border-slate-300 bg-white/60 px-7 py-4 text-base font-semibold text-ink transition-all hover:border-ice hover:text-ice"
            >
              <Phone className="h-4 w-4" /> 05 35 75 98 03
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="mt-8 flex items-center gap-3 text-sm text-slate-500"
          >
            <span className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
              ))}
            </span>
            <span>
              <strong className="text-ink">EXCELLENT</strong> · 14 avis Google
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 1.04, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.4, ease }}
          className="relative"
        >
          <div className="overflow-hidden rounded-[2rem] border border-white bg-white shadow-card">
            <img
              src={variant.image}
              alt={variant.alt}
              className="aspect-[4/3] h-full w-full object-cover sm:aspect-[16/11]"
              data-testid="clim-hero-image"
            />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="absolute -bottom-5 left-5 flex items-center gap-4 rounded-2xl border border-glacier-border bg-white/95 px-5 py-4 shadow-card backdrop-blur sm:left-8"
            data-testid="clim-hero-widget"
          >
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-ice-light text-ice">
              <Snowflake className="h-5 w-5" />
            </span>
            <div>
              <p className="font-display text-2xl font-extrabold leading-none text-ink" data-testid="clim-hero-widget-value">
                {variant.widget.value}
              </p>
              <p className="mt-1 text-xs text-slate-500">{variant.widget.caption}</p>
            </div>
          </motion.div>
          <div className="absolute left-5 top-5 rounded-full bg-forest px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-white shadow-lg sm:left-8">
            Daikin · RGE
          </div>
        </motion.div>
      </div>
    </section>
  );
}
